import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Badge } from './badge';
import { Input } from './input';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * MultiSelect with chips/tags. Lightweight, no external dependency beyond shadcn primitives.
 *
 * Props:
 *   value:      string[] of selected values
 *   onChange:   (next: string[]) => void
 *   options:    Array<{ value: string, label: string }>
 *   placeholder: string
 *   testId:     string (optional)
 *   disabled:   boolean
 */
export const MultiSelect = ({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select...',
  testId,
  disabled = false,
  className,
  emptyText = 'No options available',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (val) => {
    const next = value.includes(val) ? value.filter((v) => v !== val) : [...value, val];
    onChange?.(next);
  };

  const removeOne = (e, val) => {
    e.stopPropagation();
    onChange?.(value.filter((v) => v !== val));
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full justify-between min-h-10 h-auto py-2 px-3 font-normal hover:bg-white',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1 items-center">
            {selectedOptions.length === 0 ? (
              <span className="text-slate-500 text-sm">{placeholder}</span>
            ) : (
              selectedOptions.map((opt) => (
                <Badge
                  key={opt.value}
                  variant="secondary"
                  className="bg-slate-100 text-slate-800 hover:bg-slate-200 gap-1 pl-2 pr-1 py-0.5 text-xs"
                >
                  <span className="truncate max-w-[160px]">{opt.label}</span>
                  {!disabled && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => removeOne(e, opt.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') removeOne(e, opt.value); }}
                      className="ml-0.5 inline-flex items-center justify-center rounded-sm hover:bg-slate-300 p-0.5 cursor-pointer"
                      data-testid={testId ? `${testId}-remove-${opt.value}` : undefined}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  )}
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="h-8 pl-7 text-sm"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-500">{emptyText}</div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = value.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-100 cursor-pointer text-left',
                    isSelected && 'bg-slate-50'
                  )}
                  data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded border',
                      isSelected ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-300'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1 truncate">{opt.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelect;
